import { IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { LazyPartnerIdFormComponent } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/dataCollector/FormComponents/PartnerIdForm'
import React from 'react'
import { useSelector } from 'react-redux'

import { Roles } from 'clickNgo/configs/roles'
import PartnerContacts, { PartnerContactsConfig } from 'clickNgo/widgets/partnerContacts'
import { ContactTypeSelectorSegment } from 'clickNgo/widgets/partnerContacts/configs'
import { partnerContactsTranslation } from 'clickNgo/widgets/partnerContacts/translation'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import {
  ContractData,
  ContractDataRequestor,
} from 'onboarding/components/widgets/public/forms/contract'
import { LNRChampion } from 'onboarding/components/widgets/public/forms/lnr/LNRChampion'
import { LNRData } from 'onboarding/components/widgets/public/forms/lnr/LNRData'
import { RequestorFillAPJ } from 'onboarding/components/widgets/public/forms/requestorFillAPJ/RequestorFillAPJ'
import { RequestorFillAPJT1 } from 'onboarding/components/widgets/public/forms/requestorFillAPJT1/RequestorFillAPJT1'
import { LazyRequestorFillEmeaComponent } from 'onboarding/components/widgets/public/forms/requestorFillEMEA/RequestorFillEMEA'
import { RequestorFillLAT1 } from 'onboarding/components/widgets/public/forms/requestorFillLAT1/RequestorFillLAT1'
import { isActionDataEditable } from 'onboarding/modules/action/domain/helpers/isActionDataEditable'
import {
  COMPETITOR_BAD_ACTOR_REVIEW_FORM,
  CompetitorBadActorReviewForm,
} from 'onboarding/modules/competitor-bad-actor-review-form'

import { AmplifyDashboard } from './AmplifyDashboard'
import { AffiliatesForm } from './FormComponents/AffiliatesForm'
import { CDRCriteria } from './FormComponents/CDRCriteria'
import { ContactData } from './FormComponents/ContactData'
import { ContractValidateApj } from './FormComponents/ContractValidateApj'
import { ContractValidateEmea } from './FormComponents/ContractValidateEmea'
import { ExceptionForm } from './FormComponents/ExceptionForm'
import { LocationForm } from './FormComponents/LocationForm'
import { PartnerShipLA } from './FormComponents/PartnerShipLA'
import { PartnerShipNA } from './FormComponents/PartnerShipNA'
import { PowerCriteria } from './FormComponents/PowerCriteria'
import { PowerServiceCriteria } from './FormComponents/PowerServiceCriteria'
import {
  IRequestDetailPartnerFormWrapperProps,
  RequestDetailPartnerFormWrapper,
} from './FormComponents/requestDetailPartnerFormWrapper/RequestDetailPartnerFormWrapper'

import styles from './FormSelector.module.scss'

export enum EFormSource {
  ContractFollowUp = '720',
  LegalAndRegulatory = '721',
  RequestorToFillApjT1OrEMEAOrApj = '736',
  RequestorToFillLAT1 = '803',
  PartnershipDetailsLA = '806',
  PartnershipDetailsNA = '807',
}

const ONBOARDING_CONFIG: Required<PartnerContactsConfig> = {
  isOnboarding: true,
  isPartnerSelectorVisible: false,
  isDraftContactCreationEnabled: true,
  defaultSegment: ContactTypeSelectorSegment.SelectedPartner,
  segments: [
    {
      key: ContactTypeSelectorSegment.SelectedPartner,
      text: partnerContactsTranslation.contacts.controls.segment.partner,
    },
    {
      key: ContactTypeSelectorSegment.AllContacts,
      text: partnerContactsTranslation.contacts.controls.segment.all,
    },
    {
      key: ContactTypeSelectorSegment.ProfilingRequests,
      text: partnerContactsTranslation.contacts.controls.segment.profiling,
    },
    {
      key: ContactTypeSelectorSegment.NewContacts,
      text: partnerContactsTranslation.contacts.controls.segment.new,
    },
  ],
}

interface IProps {
  viewModel: string
  partnerId: string
  componentProps?: IObjectAny
  flexWidth: string
  audienceSettings: IAudienceCondition
  actionId?: number
  startDate?: string
  disabled?: boolean
  ridForForm: number
  parentRequestId: number | null
  currentRequest: IRequest
  formTitle?: string
  actionStatus?: string
}

const FormSwitch = (props: IProps) => {
  const requestIdAsString = props.ridForForm.toString()
  const parentRequestIdAsString = props.parentRequestId?.toString() ?? null
  const appSource =
    props.currentRequest.Type !== 'Onboarding'
      ? `Onboarding ${props.currentRequest.Type}`
      : props.currentRequest.Type
  const newProps = {
    ...props.componentProps,
    PartnerID: props.partnerId,
  }
  const { audienceSettings } = props

  switch (props.viewModel) {
    case 'list_of_contacts':
      return (
        <PartnerContacts
          partnerId={props.partnerId}
          source={appSource}
          onboardingRequestId={props.currentRequest.EntryId}
          config={ONBOARDING_CONFIG}
        />
      )
    case 'partner_id_form':
      return <LazyPartnerIdFormComponent {...newProps} />
    case 'partner_data':
      return props.partnerId ? (
        <VisualAudienceExtended<IRequestDetailPartnerFormWrapperProps>
          component={RequestDetailPartnerFormWrapper}
          write={audienceSettings}
          partnerId={props.partnerId}
          disabled={props.disabled}
          onboardingRequestId={props.currentRequest.EntryId}
          appSource={appSource}
          request={props.currentRequest}
        />
      ) : (
        <div>Partner is not provided</div>
      )
    case 'contact_data_form':
      return <ContactData {...(newProps as any)} />
    case 'lnr_data_form':
      return (
        <LNRData
          {...(newProps as any)}
          flexWidth={props.flexWidth}
          audienceSettings={audienceSettings}
          actionId={props.actionId}
          startDate={props.startDate}
          disabled={props.disabled}
          appSource={appSource}
          actionStatus={props.actionStatus}
          requestId={requestIdAsString}
          parentRequestId={parentRequestIdAsString}
          partnerId={props.partnerId}
        />
      )
    case 'lnr_champion':
      return (
        <LNRChampion
          requestId={requestIdAsString}
          partnerId={props.partnerId}
          isDisabled={Boolean(
            props.disabled || (props.actionStatus && !isActionDataEditable(props.actionStatus))
          )}
        />
      )
    case 'contract_data_form':
      return (
        <ContractData
          {...(newProps as any)}
          audienceSettings={audienceSettings}
          requestId={parentRequestIdAsString ?? requestIdAsString}
        />
      )
    case 'contract_data_requestor_form':
      return (
        <ContractDataRequestor
          {...(newProps as any)}
          audienceSettings={audienceSettings}
          requestId={parentRequestIdAsString ?? requestIdAsString}
        />
      )
    case 'contract_validate_apj':
      return (
        <ContractValidateApj
          metrics={props.componentProps?.metrics}
          flexWidth={props.flexWidth}
          appSource={appSource}
          requestId={requestIdAsString}
          parentRequestId={props.parentRequestId}
          disabled={!(props.actionStatus && isActionDataEditable(props.actionStatus))}
          audienceSettings={props.audienceSettings}
        />
      )
    case 'contract_validate_emea':
      return (
        <ContractValidateEmea
          metrics={props.componentProps?.metrics}
          flexWidth={props.flexWidth}
          appSource={appSource}
          requestId={requestIdAsString}
          partnerID={props.partnerId}
          parentRequestId={props.parentRequestId}
          disabled={!(props.actionStatus && isActionDataEditable(props.actionStatus))}
          audienceSettings={props.audienceSettings}
        />
      )
    case 'widget-pf-eligibility':
      return <AmplifyDashboard {...(newProps as any)} />
    case 'power-criteria':
      return (
        <PowerCriteria
          {...(newProps as any)}
          flexWidth={props.flexWidth}
          rid={props.parentRequestId ?? props.ridForForm}
          appSource={appSource}
        />
      )
    case 'synergy-criteria':
      return (
        <PowerCriteria
          {...(newProps as any)}
          flexWidth={props.flexWidth}
          rid={props.parentRequestId ?? props.ridForForm}
          appSource={appSource}
        />
      )
    case 'power-services-criteria':
      return (
        <PowerServiceCriteria
          {...(newProps as any)}
          flexWidth={props.flexWidth}
          rid={props.parentRequestId ?? props.ridForForm}
          appSource={appSource}
        />
      )
    case 'cdr-criteria':
      return (
        <CDRCriteria
          {...(newProps as any)}
          rid={props.parentRequestId ?? props.ridForForm}
          appSource={appSource}
        />
      )
    case 'location-form':
      return (
        <LocationForm
          {...(newProps as any)}
          appSource={appSource}
          RequestId={parentRequestIdAsString ?? requestIdAsString}
        />
      )
    case 'affiliates-form':
      return (
        <AffiliatesForm
          {...(newProps as any)}
          appSource={appSource}
          RequestId={parentRequestIdAsString ?? requestIdAsString}
        />
      )
    case 'exception_form':
      return (
        <ExceptionForm
          flexWidth={props.flexWidth}
          requestId={requestIdAsString}
          disabled={props.disabled}
          audienceSettings={audienceSettings}
        />
      )
    case 'req_fill_emea_data_form':
      return (
        <LazyRequestorFillEmeaComponent
          audienceSettings={audienceSettings}
          requestId={requestIdAsString}
          disabled={props.disabled}
        />
      )
    case 'req_fill_apj_data_form':
      return (
        <RequestorFillAPJ
          audienceSettings={audienceSettings}
          requestId={requestIdAsString}
          disabled={props.disabled}
        />
      )
    case 'req_fill_apj_t1_data_form':
      return (
        <RequestorFillAPJT1
          audienceSettings={audienceSettings}
          requestId={requestIdAsString}
          disabled={props.disabled}
        />
      )
    case 'req_fill_la_t1_data_form':
      return (
        <RequestorFillLAT1
          audienceSettings={audienceSettings}
          requestId={props.ridForForm}
          disabled={props.disabled}
        />
      )
    case 'partner_ship_la':
      return (
        <PartnerShipLA
          audienceSettings={audienceSettings}
          requestId={requestIdAsString}
          partnerId={props.partnerId}
          disabled={props.disabled}
        />
      )
    case 'partner_ship_na':
      return (
        <PartnerShipNA
          audienceSettings={audienceSettings}
          requestId={requestIdAsString}
          disabled={props.disabled}
        />
      )
    case COMPETITOR_BAD_ACTOR_REVIEW_FORM:
      return (
        <CompetitorBadActorReviewForm
          requestId={props.ridForForm}
          parameters={props.componentProps}
        />
      )
    default:
      return (
        <div>
          {'Form '}
          <b>{props.formTitle || props.viewModel}</b>
          {` doesn't exist`}
        </div>
      )
  }
}

export const FormSelector = (props: IProps) => {
  const { roleIds: roles } = useSelector(selectUser)

  if (
    props.disabled &&
    props.viewModel !== 'lnr_data_form' &&
    props.viewModel !== 'partner_data' &&
    !roles.find((role) => role === Roles.CONTRACT_OPERATIONS || role === Roles.SUPER_ADMIN_ROLE)
  ) {
    return (
      <div className={styles.disabledContent}>
        <FormSwitch {...props} />
      </div>
    )
  }

  return <FormSwitch {...props} />
}
